import { Alert, Divider, Group, Loader, Paper, Stack, Text } from "@mantine/core";
import { queryOptions, useQuery } from "@tanstack/react-query";
import * as turf from "@turf/turf";
import { httpPostGraphql } from "shared/api/httpClient";
import { useComposeFormContext } from "shared/components/message/compose/context";
import { useGeoAudienceEditor } from "shared/components/message/compose/geoAudience/useGeoAudienceEditor";
import { useFormSubscription } from "shared/hooks/useFormSubscription";
import { graphql } from "src/gql";

function unionFeatures(features: GeoJSON.Feature<GeoJSON.Polygon>[]) {
    if (features.length === 0) return null;
    if (features.length === 1) return features[0];
    return turf.union(turf.featureCollection(features));
}

export const GeoAudienceSummary = () => {
    const form = useComposeFormContext();
    const features = useFormSubscription(form, "audience.features");
    const senderId = useFormSubscription(form, "sender.id");
    const { tab, selectedSenderFilters, senderGeoFilters, isLoading, geoFilters } = useGeoAudienceEditor();

    const drawnFeature = unionFeatures(features);
    const senderBoundaryFeatures = senderGeoFilters.map((filter) => {
        const geometry = JSON.parse(filter.boundary) as GeoJSON.Polygon;
        return turf.polygon(geometry.coordinates);
    });
    const senderBoundaryUnion = unionFeatures(senderBoundaryFeatures);
    const selectedFeature =
        tab === "Draw"
            ? senderBoundaryUnion
            : unionFeatures(selectedSenderFilters.map((filter) => turf.polygon(filter.geometry.coordinates)));
    const intersection = drawnFeature && selectedFeature ? turf.intersect(turf.featureCollection([drawnFeature, selectedFeature])) : null;
    const intersectionGeometry: GeoJSON.MultiPolygon | null =
        intersection?.geometry.type === "MultiPolygon"
            ? intersection.geometry
            : intersection?.geometry.type === "Polygon"
              ? { type: "MultiPolygon", coordinates: [intersection.geometry.coordinates] }
              : null;

    const { data: intersectionCount, isPending } = useQuery(queries.intersectionCount(senderId, intersectionGeometry));

    const showCustomAreas = features.length > 0;
    const showPredefinedAreas = selectedSenderFilters.length > 0 || (isLoading && geoFilters.length > 0);
    const showOverlapNote = showCustomAreas && showPredefinedAreas;
    const resolvedIntersectionCount = !!intersectionGeometry ? (intersectionCount ?? 0) : 0;

    return (
        <>
            {showCustomAreas && (
                <Paper withBorder p="xs">
                    <Group justify="space-between">
                        <Text size="xs" fw={600}>
                            Custom Areas
                        </Text>
                        <Text size="xs" fw={600}>
                            Estimated Users
                        </Text>
                    </Group>
                    <Divider my="xs" />
                    <Group justify="space-between">
                        <Text size="sm">Drawn areas</Text>
                        <Text size="sm" fw={600}>
                            {isPending && !!intersectionGeometry ? <Loader size="xs" /> : resolvedIntersectionCount.toLocaleString()}
                        </Text>
                    </Group>
                </Paper>
            )}
            {showPredefinedAreas && (
                <Paper withBorder p="xs">
                    <Group justify="space-between">
                        <Text size="xs" fw={600}>
                            Predefined Areas
                        </Text>
                        <Text size="xs" fw={600}>
                            Estimated Users
                        </Text>
                    </Group>
                    <Divider my="xs" />
                    <Stack gap="xs">
                        {isLoading && selectedSenderFilters.length === 0 ? (
                            <Group justify="center" p="xs">
                                <Loader size="sm" />
                            </Group>
                        ) : (
                            selectedSenderFilters.map((filter, index) => (
                                <Stack key={filter.id} gap="xs">
                                    <Group justify="space-between">
                                        <Text size="sm">{filter.name}</Text>
                                        <Text size="sm" fw={600}>
                                            {filter.projectedUsersWithin.toLocaleString()}
                                        </Text>
                                    </Group>
                                    {index < selectedSenderFilters.length - 1 && <Divider />}
                                </Stack>
                            ))
                        )}
                    </Stack>
                    {showOverlapNote && (
                        <Alert mt="md" p="sm" color="blue">
                            <Text size="xs">
                                If recipients are in overlapping custom and predefined areas, they will be counted in both estimates, but
                                alerted only once.
                            </Text>
                        </Alert>
                    )}
                </Paper>
            )}
        </>
    );
};

const queries = {
    intersectionCount: (senderId: string, geometry: GeoJSON.MultiPolygon | null) =>
        queryOptions({
            enabled: !!senderId && !!geometry,
            queryKey: ["sender", "intersection-count", senderId, geometry ? JSON.stringify(geometry) : "none"],
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query GeoAudienceSummaryIntersectionCount($senderId: UUID!, $within: GeoMultiPolygon!) {
                            sender(id: $senderId) {
                                id
                                projectedUsersWithin(within: $within)
                            }
                        }
                    `),
                    {
                        senderId,
                        within: JSON.stringify(geometry),
                    },
                ),
            select: (data) => data.sender?.projectedUsersWithin ?? 0,
        }),
};
